import { useMemo } from 'react';

/**
 * Custom hook for certificate management and validation
 */
export const useCertificates = (certificates = []) => {
  // Parse certificate validity date
  const dateParser = date => {
    const exactDate = new Date(date);
    const actualDate = `${exactDate.getFullYear()}-${exactDate.getMonth() + 1}-${
      exactDate.getDate() + 1
    }`;
    return actualDate;
  };

  // Check if certificate is valid (not expired)
  const isCertificateValid = certificate => {
    if (!certificate.validTo) return false;
    const now = new Date();
    const validTo = new Date(certificate.validTo);
    return validTo > now;
  };

  // Get certificate status
  const getCertificateStatus = certificate => {
    if (!certificate.validTo) return 'unknown';

    const now = new Date();
    const validTo = new Date(certificate.validTo);
    const daysUntilExpiry = Math.ceil((validTo - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) return 'expired';
    if (daysUntilExpiry <= 30) return 'expiring_soon';
    return 'valid';
  };

  // Enhanced certificates with additional metadata
  const enhancedCertificates = useMemo(() => {
    return certificates.map((cert, index) => ({
      ...cert,
      id: index,
      isValid: isCertificateValid(cert),
      status: getCertificateStatus(cert),
      formattedValidTo: dateParser(cert.validTo),
      displayName: cert.CN || `Certificate ${index + 1}`,
    }));
  }, [certificates]);

  // Filter certificates by status
  const validCertificates = enhancedCertificates.filter(cert => cert.isValid);
  const expiredCertificates = enhancedCertificates.filter(
    cert => !cert.isValid,
  );
  const expiringSoonCertificates = enhancedCertificates.filter(
    cert => cert.status === 'expiring_soon',
  );

  // Statistics
  const stats = {
    total: enhancedCertificates.length,
    valid: validCertificates.length,
    expired: expiredCertificates.length,
    expiringSoon: expiringSoonCertificates.length,
  };

  // Get certificate by index
  const getCertificateByIndex = index => {
    return enhancedCertificates[index] || null;
  };

  // Get the best certificate to use (valid, not expiring soon)
  const getRecommendedCertificate = () => {
    const validNotExpiring = validCertificates.filter(
      cert => cert.status === 'valid',
    );
    return validNotExpiring[0] || validCertificates[0] || null;
  };

  return {
    // Raw data
    certificates: enhancedCertificates,

    // Filtered data
    validCertificates,
    expiredCertificates,
    expiringSoonCertificates,

    // Statistics
    stats,

    // Utilities
    getCertificateByIndex,
    getRecommendedCertificate,
    isCertificateValid,
    getCertificateStatus,
    dateParser,
  };
};
